import axios from "axios";
import { createApiClient } from "../utils/api";
import { paramsObjectToQueryString } from "../utils/helpers";
import { userApiRoutes } from "./routes";
import { podApiRoutes } from "./routes";

export const userServices = {
  user: {
    getMe: async () => {
      const response = await createApiClient().get(userApiRoutes.user.getMe);
      return response.data;
    },
    investment: async () => {
      const response = await createApiClient().get(
        userApiRoutes.user.getInvestments,
      );
      return response.data;
    },
    podInvestment: async () => {
      const response = await createApiClient().get(
        userApiRoutes.user.getPodInvestments,
      );
      return response.data;
    },
    getUserById: async (userId: string) => {
      const response = await createApiClient().get(
        userApiRoutes.user.getUserById(userId),
      );
      return response.data;
    },
    exportUsers: async ({
      format = "csv",
      startDate,
      endDate,
      search,
    }: {
      format?: "csv" | "xls" | "doc";
      startDate?: string; // YYYY-MM-DD
      endDate?: string; // YYYY-MM-DD
      search?: string; // Search query
    }) => {
      const payload = { format, startDate, endDate, search };

      const response = await createApiClient().get(
        userApiRoutes.user.exportUsers + paramsObjectToQueryString(payload),
        {
          responseType: "blob", // Important for file downloads
        },
      );

      const blob = response.data;

      const downloadUrl = window.URL.createObjectURL(blob);

      return downloadUrl;
    },
    getAllUsers: async (payload: any) => {
      const response = await createApiClient().get(
        userApiRoutes.user.getAllUsers + paramsObjectToQueryString(payload),
      );
      return response.data;
    },
    updateUserPersonalInfo: async (userId: string, payload: any) => {
      const response = await createApiClient().patch(
        userApiRoutes.user.updateUserPersonal(userId),
        payload,
      );
      return response.data;
    },
    updateUserEmployerInfo: async (userId: string, payload: any) => {
      const response = await createApiClient().patch(
        userApiRoutes.user.updateUserEmployer(userId),
        payload,
      );
      return response.data;
    },
    changePassword: async (payload: any, userId: string) => {
      const response = await createApiClient().patch(
        userApiRoutes.user.changePassword(userId),
        payload,
      );
      return response.data;
    },
    countAll: async () => {
      const response = await createApiClient().get(userApiRoutes.user.countAll);
      return response.data;
    },
    getUserPods: async (payload: any) => {
      const response = await createApiClient().get(
        `${podApiRoutes.getUserPods}?status=${payload?.status}`,
      );
      return response.data;
    },
    checkUserPodStatus: async (id: string) => {
      const response = await createApiClient().get(
        userApiRoutes.user.checkUserPodStatus(id),
      );
      return response.data;
    },

    deleteUser: async (userId: string) => {
      const response = await createApiClient().delete(
        userApiRoutes.user.deleteUser(userId),
      );
      return response.data;
    },
    setEquifaxConsent: async (option: boolean) => {
      const response = await createApiClient().patch(
        userApiRoutes.user.setEquifaxConsent,
        { option },
      );
      return response.data;
    },
  },
  kyc: {
    getKYC: async (userId: string) => {
      const response = await createApiClient().get(
        userApiRoutes.kyc.getKYC(userId),
      );
      return response.data;
    },
    createKYC: async (userId: string, payload: any) => {
      const response = await createApiClient().post(
        userApiRoutes.kyc.createKYC(userId),
        payload,
      );
      return response.data;
    },
    updateKYC: async (userId: string, payload: any) => {
      const response = await createApiClient().patch(
        userApiRoutes.kyc.updateKYC(userId),
        payload,
      );
      return response.data;
    },
  },
  payoutInfo: {
    get: async (userId: string) => {
      const response = await createApiClient().get(
        userApiRoutes.payoutInfo.get(userId),
      );
      return response.data;
    },
    submit: async (userId: string, payload: any) => {
      const response = await createApiClient().patch(
        userApiRoutes.payoutInfo.submit(userId),
        payload,
      );
      return response.data;
    },
    adminList: async () => {
      const response = await createApiClient().get(
        userApiRoutes.payoutInfo.adminList,
      );
      return response.data;
    },
    adminExport: async ({
      format = "csv",
    }: {
      format?: "csv" | "xls" | "doc";
    }) => {
      const response = await createApiClient().get(
        `${userApiRoutes.payoutInfo.adminExport}?format=${format}`,
        { responseType: "blob" },
      );
      return window.URL.createObjectURL(response.data);
    },
  },
  bank: {
    getAllBanks: async () => {
      const response = await createApiClient().get(
        userApiRoutes.bank.getAllBanks,
      );
      return response.data;
    },
    getBank: async (bankId: string) => {
      const response = await createApiClient().get(
        userApiRoutes.bank.getBank(bankId),
      );
      return response.data;
    },
    createBank: async (payload: any) => {
      const response = await createApiClient().post(
        userApiRoutes.bank.createBank,
        payload,
      );
      return response.data;
    },
    updateBank: async (bankId: string, payload: any) => {
      const response = await createApiClient().patch(
        userApiRoutes.bank.updateBank(bankId),
        payload,
      );
      return response.data;
    },
    deleteBank: async (bankId: string) => {
      const response = await createApiClient().delete(
        userApiRoutes.bank.deleteBank(bankId),
      );
      return response.data;
    },
  },
  guarantor: {
    getAllGuarantors: async (userId: string) => {
      const response = await createApiClient().get(
        userApiRoutes.guarantor.getAllGuarantors(userId),
      );
      return response.data;
    },
    getGuarantor: async (userId: string, guarantorId: string) => {
      const response = await createApiClient().get(
        userApiRoutes.guarantor.getGuarantor(guarantorId),
      );
      return response.data;
    },
    addGuarantor: async (userId: string, payload: any) => {
      const response = await createApiClient().post(
        userApiRoutes.guarantor.addGuarantor,
        payload,
      );
      return response.data;
    },
    updateGuarantor: async (
      userId: string,
      guarantorId: string,
      payload: any,
    ) => {
      const response = await createApiClient().patch(
        userApiRoutes.guarantor.updateGuarantor(guarantorId),
        payload,
      );
      return response.data;
    },
  },
  investmentAssessment: {
    getAssessment: async (podType: string) => {
      const response = await createApiClient().get(
        userApiRoutes.investmentAssessment.getAssessment(podType),
      );
      return response.data;
    },
    submitAssessment: async (payload: any) => {
      const response = await createApiClient().post(
        userApiRoutes.investmentAssessment.submitAssessment,
        payload,
      );
      return response.data;
    },
    getUserResponses: async (userId: string) => {
      const response = await createApiClient().get(
        userApiRoutes.investmentAssessment.getUsersResponses(userId),
      );
      return response.data;
    },
  },
};
